import React, { FC, type PropsWithChildren, useContext } from "react";

import {
  Avatar,
  Button,
  Box,
  Menu,
  Center,
  Portal,
  useMediaQuery,
} from "@chakra-ui/react";
import { TenantContext, UserContext } from "@mtb/mtb-core";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "@emotion/react";

const AvatarImg = Avatar.Image as React.FC<
  React.ImgHTMLAttributes<HTMLImageElement>
>;

const MenuTrigger = Menu.Trigger as React.FC<
  PropsWithChildren<{ asChild?: boolean }>
>;
const MenuPositioner = Menu.Positioner as React.FC<PropsWithChildren>;
const MenuContent = Menu.Content as React.FC<PropsWithChildren>;
const MenuItem = Menu.Item as React.FC<
  PropsWithChildren<{ value: string; onSelect?: () => void }>
>;
const MenuItemText = Menu.ItemText as React.FC<PropsWithChildren>;

export const AvatarMenu: FC = () => {
  const { user, setUser } = useContext(UserContext);
  const [isLargerThan640] = useMediaQuery(["(min-width: 640px)"]);
  const theme = useTheme();
  const { tenant } = useContext(TenantContext);
  const { loginWithPopup, logout } = useAuth0();
  const onLoginClick = async () => {
    await loginWithPopup({
      authorizationParams: {
        prompt: "login",
        tenant: tenant?.tenantKey,
      },
    });
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
  };
  let name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  if (!name) {
    name = "profile";
  }

  return (
    <Menu.Root>
      {user ? (
        <MenuTrigger asChild>
          <Button
            rounded="full"
            variant="ghost"
            cursor="pointer"
            minW={0}
            display="flex"
          >
            <Box display="flex">
              <Avatar.Root size="md" backgroundColor="gray.100">
                <Avatar.Fallback />
                <AvatarImg src={user?.photoUrl} alt="" />
              </Avatar.Root>
              {isLargerThan640 && (
                <Box mt="3" ml="2px">
                  {name}
                </Box>
              )}
            </Box>
          </Button>
        </MenuTrigger>
      ) : (
        <Button
          onClick={onLoginClick}
          variant="plain"
          color={theme.colors.primary}
        >
          Login
        </Button>
      )}
      {user && (
        <Portal>
          <MenuPositioner>
            <MenuContent>
              <Center>
                <Avatar.Root size="2xl" backgroundColor="gray.100">
                  <Avatar.Fallback />
                  <AvatarImg src={user.photoUrl} alt="" />
                </Avatar.Root>
              </Center>
              {name && (
                <Center>
                  <Box as="p" color={theme.colors.secondary}>
                    {name}
                  </Box>
                </Center>
              )}
              <Menu.Separator />
              <MenuItem
                value="logout"
                onSelect={() => {
                  void handleLogout();
                }}
              >
                <MenuItemText>Logout</MenuItemText>
              </MenuItem>
            </MenuContent>
          </MenuPositioner>
        </Portal>
      )}
    </Menu.Root>
  );
};
